import { Play, Pause, SpotifyLogo, SoundcloudLogo, LinkSimple } from '@phosphor-icons/react';
import { usePlayer } from '@/context/PlayerContext';
import { motion } from 'framer-motion';

export const TrackCard = ({ track, index = 0, showArtist = false, onPlay }) => {
  const { currentTrack, isPlaying, playTrack, togglePlay } = usePlayer();
  const isCurrentTrack = currentTrack?.id === track.id;
  const isExternal = !!track.external_url;

  const handlePlay = () => {
    if (onPlay) {
      onPlay(track);
    } else if (isCurrentTrack && !isExternal) {
      togglePlay();
    } else {
      playTrack(track);
    }
  };

  const getExternalIcon = () => {
    if (!track.external_url) return null;
    if (track.external_url.includes('spotify')) return <SpotifyLogo size={16} weight="fill" />;
    if (track.external_url.includes('soundcloud')) return <SoundcloudLogo size={16} weight="fill" />;
    return <LinkSimple size={16} />;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`track-row group flex items-center gap-4 p-3 rounded-lg cursor-pointer ${
        isCurrentTrack ? 'bg-white/10' : ''
      }`}
      onClick={handlePlay}
      data-testid={`track-card-${track.id}`}
    >
      {/* Play Button / Track Number */}
      <div className="w-10 h-10 flex items-center justify-center">
        <button
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
            isCurrentTrack
              ? 'bg-[#CCFF00] text-black'
              : 'bg-[#1A1A1A] text-white group-hover:bg-[#CCFF00] group-hover:text-black'
          }`}
          data-testid={`play-track-${track.id}`}
        >
          {isCurrentTrack && isPlaying && !isExternal ? (
            <Pause size={18} weight="fill" />
          ) : (
            <Play size={18} weight="fill" />
          )}
        </button>
      </div>

      {/* Track Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`font-medium truncate ${isCurrentTrack ? 'text-[#CCFF00]' : 'text-white'}`}>
            {track.title}
          </p>
          {isExternal && (
            <span className="text-[#A1A1AA]">{getExternalIcon()}</span>
          )}
        </div>
        {showArtist && track.musician && (
          <p className="text-sm text-[#A1A1AA] truncate">{track.musician.name}</p>
        )}
      </div>

      {/* Genre */}
      {track.genre && (
        <span className="hidden sm:block text-xs text-[#A1A1AA] bg-white/5 px-2 py-1 rounded-full">
          {track.genre}
        </span>
      )}

      {/* Duration */}
      <span className="text-sm text-[#A1A1AA] w-12 text-right">
        {formatDuration(track.duration)}
      </span>

      {/* Plays */}
      <span className="hidden sm:block text-sm text-[#A1A1AA] w-16 text-right">
        {track.plays || 0} plays
      </span>
    </motion.div>
  );
};
